import { useEffect, useMemo, useState } from "react";
import {
    FaArchive, FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaCopy, FaEye, FaEyeSlash,
    FaEdit, FaExternalLinkAlt, FaInfoCircle, FaKey, FaLink, FaPlus, FaServer, FaUser,
} from "react-icons/fa";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldInput, FieldSelect, FormGrid } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import {
    CREDENTIALS_CONSTANTS, PROJECTS_CONSTANTS, SUBPROJECTS_CONSTANTS,
} from "../_MODULES/_API";
import { VAULT_CONSTANTS } from "../../_Companies/_MODULES/_API";
import { useProjectAccess } from "../_Hooks/useProjectAccess";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { OPTIONS_CREDENTIAL_TYPE, OPTIONS_VAULT_ENTRY_TYPE, onFindOptionLabel } from "../../../_Catalogs.Views";

const SOURCE_PROJECT = "PROJECT_CREDENTIAL";
const SOURCE_VAULT = "COMPANY_VAULT";
const SECRET_TOKEN = "TOKEN";
const SECRET_SECRET = "SECRET";
const MAX_QUICK_LINKS = 5;

const EMPTY_LINK = Object.freeze({ source: "", id: "", secretField: SECRET_TOKEN });

const onQuickDraft = (record = null) => ({
    title: record?.title || "Acceso r\u00e1pido de deploy",
    username: record?.username || "",
    token: "",
    host: record?.host || record?.url || "",
});

const onAsArray = (value) => Array.isArray(value) ? value : [];
const onPlainText = (value) => String(value ?? "")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const onCredentialNameKey = (value) => onPlainText(value)
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("es");

const onFlattenVaultEntries = (groups = []) => {
    const out = [];
    const walk = (entries, group) => {
        onAsArray(entries).forEach((entry) => {
            out.push({
                ...entry,
                __source: SOURCE_VAULT,
                __groupId: group.id,
                __groupName: group.name,
                __groupHasPin: !!group.hasAccessPin,
            });
            walk(entry.children, group);
        });
    };
    onAsArray(groups).forEach((group) => walk(group.entries, group));
    return out;
};

const onSourceKey = (source, id) => source && id ? `${source}:${id}` : "";
const onParseSourceKey = (value = "") => {
    const [source = "", ...idParts] = String(value).split(":");
    return { source, id: idParts.join(":") };
};

const onSecretOptions = (record) => {
    if (!record) return [];
    const options = [];
    if (record.hasToken || record.token) options.push({ value: SECRET_TOKEN, label: "Token / API key" });
    if (record.hasSecret || record.secret) options.push({ value: SECRET_SECRET, label: "Contraseña / secreto" });
    return options;
};

const onCopy = async (value, label = "Dato") => {
    const text = String(value ?? "");
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        REACT_HOT_TOAST_SUCCESS(`${label} copiado`);
    } catch (_) {
        REACT_HOT_TOAST_ERROR(`No se pudo copiar ${label.toLowerCase()}`);
    }
};

const onReadLinks = (record = {}) => [
    {
        source: record?.stepQuickCredentialSource || "",
        id: record?.stepQuickCredentialId || "",
        secretField: record?.stepQuickCredentialSecretField || SECRET_TOKEN,
    },
    {
        source: record?.stepQuickCredential2Source || "",
        id: record?.stepQuickCredential2Id || "",
        secretField: record?.stepQuickCredential2SecretField || SECRET_TOKEN,
    },
    {
        source: record?.stepQuickCredential3Source || "",
        id: record?.stepQuickCredential3Id || "",
        secretField: record?.stepQuickCredential3SecretField || SECRET_TOKEN,
    },
    {
        source: record?.stepQuickCredential4Source || "",
        id: record?.stepQuickCredential4Id || "",
        secretField: record?.stepQuickCredential4SecretField || SECRET_TOKEN,
    },
    {
        source: record?.stepQuickCredential5Source || "",
        id: record?.stepQuickCredential5Id || "",
        secretField: record?.stepQuickCredential5SecretField || SECRET_TOKEN,
    },
];

const onNormalizeLinks = (links = []) => {
    const compact = onAsArray(links)
        .filter((item) => item?.source && item?.id)
        .slice(0, MAX_QUICK_LINKS)
        .map((item) => ({
            source: item.source || "",
            id: item.id || "",
            secretField: item.secretField || SECRET_TOKEN,
        }));
    while (compact.length < MAX_QUICK_LINKS) compact.push({ ...EMPTY_LINK });
    return compact;
};

const onLinksPatch = (links = []) => {
    const normalized = onNormalizeLinks(links);
    const [one, two, three, four, five] = normalized;
    return {
        stepQuickCredentialSource: one.source || null,
        stepQuickCredentialId: one.id || null,
        stepQuickCredentialSecretField: one.source ? (one.secretField || SECRET_TOKEN) : null,
        stepQuickCredential2Source: two.source || null,
        stepQuickCredential2Id: two.id || null,
        stepQuickCredential2SecretField: two.source ? (two.secretField || SECRET_TOKEN) : null,
        stepQuickCredential3Source: three.source || null,
        stepQuickCredential3Id: three.id || null,
        stepQuickCredential3SecretField: three.source ? (three.secretField || SECRET_TOKEN) : null,
        stepQuickCredential4Source: four.source || null,
        stepQuickCredential4Id: four.id || null,
        stepQuickCredential4SecretField: four.source ? (four.secretField || SECRET_TOKEN) : null,
        stepQuickCredential5Source: five.source || null,
        stepQuickCredential5Id: five.id || null,
        stepQuickCredential5SecretField: five.source ? (five.secretField || SECRET_TOKEN) : null,
    };
};

const onEnvironmentLabel = (environment) => {
    if (!environment) return "";
    if (typeof environment === "string") return environment;
    return [environment.subProjectName, environment.name].filter(Boolean).join(" · ");
};

const onSecretFlag = (field) => `has${field.charAt(0).toUpperCase()}${field.slice(1)}`;

const onReadRevealedValue = (revealed, field) => {
    if (!revealed || !field) return "";
    if (String(field).startsWith("extra:")) {
        const label = String(field).slice(6);
        return onAsArray(revealed.extra).find((item) => String(item?.label || "") === label)?.value ?? "";
    }
    return revealed[field] ?? "";
};

const onBuildMoreInfoRows = (record, revealed = null) => {
    if (!record) return [];

    const rows = [];
    const push = (field, label, value, isSecret = false, hasValue = true) => {
        if (!hasValue) return;
        const clean = value === null || value === undefined ? "" : String(value).trim();
        if (!clean && !isSecret) return;
        rows.push({ field, label, value: clean || "••••••••", isSecret });
    };

    const typeOptions = record.__source === SOURCE_VAULT ? OPTIONS_VAULT_ENTRY_TYPE : OPTIONS_CREDENTIAL_TYPE;
    push("type", "Tipo", onFindOptionLabel(typeOptions, record.type) || record.type);
    push("environment", "Entorno", onEnvironmentLabel(record.environment));
    push("email", "Correo", record.email);
    push("username", "Usuario", record.username);
    push("host", "Host / dirección", record.host);
    push("port", "Puerto", record.port);
    push("url", "URL / panel", record.url && record.url !== record.host ? record.url : "");
    push("databaseName", "Base de datos", record.databaseName);
    push("envKey", "Prefijo .env", record.envKey);

    [
        ["secret", "Contraseña"],
        ["token", "Token / API key / secreto"],
        ["connectionString", "Cadena de conexión"],
        ["sshKey", "Clave SSH / deploy key"],
    ].forEach(([field, label]) => {
        const hasValue = !!record?.[onSecretFlag(field)] || !!record?.[field] || !!onReadRevealedValue(revealed, field);
        push(field, label, onReadRevealedValue(revealed, field) || record?.[field], true, hasValue);
    });

    push("passwordHint", "Pista / palabra secreta", record.passwordHint);
    push("organization", "Organización / grupo", record.organization || record.group || record.namespace);
    push("repository", "Repositorio", record.repository || record.repositoryName);
    push("defaultBranch", "Rama por defecto", record.defaultBranch || record.branch);
    push("repositoryProjectId", "ID proyecto / repositorio", record.repositoryProjectId || record.externalProjectId);
    push("scopes", "Scopes / permisos", record.scopes || record.tokenScopes);
    push("sshCommand", "Comando SSH", record.sshCommand);
    push("sshKeygenCommand", "Generador de clave SSH", record.sshKeygenCommand);
    push("command", "Comando", record.command);
    push("commandLanguage", "Lenguaje del comando", record.commandLanguage);

    onAsArray(record.extra).forEach((item) => {
        const label = String(item?.label || "").trim();
        if (!label) return;
        const field = `extra:${label}`;
        const revealedValue = onReadRevealedValue(revealed, field);
        const raw = item?.value;
        const hasValue = raw !== null && raw !== undefined && String(raw).trim() !== "";
        push(field, label, revealedValue || raw, !!item?.isSecret, hasValue || !!revealedValue);
    });

    const files = onAsArray(record.files)
        .map((file) => file?.originalName || file?.title || file?.name)
        .filter(Boolean)
        .join(", ");
    push("files", "Archivos", files);
    push("notes", "Notas", onPlainText(record.notes));

    return rows;
};

const QuickValue = ({ icon, label, value, isSecret = false, isRevealed = false, onReveal, onCopyValue }) => (
    <div className="LStudio__app_steps_quick_field">
        <span className="LStudio__app_steps_quick_field_icon">{icon}</span>
        <div className="LStudio__app_steps_quick_field_copy">
            <span>{label}</span>
            <strong title={!isSecret || isRevealed ? String(value || "") : undefined}>
                {value || "Sin dato"}
            </strong>
        </div>
        <div className="LStudio__app_steps_quick_field_actions">
            {isSecret && (
                <button type="button" onClick={onReveal} title={isRevealed ? "Ocultar" : "Revelar"}>
                    {isRevealed ? <FaEyeSlash /> : <FaEye />}
                </button>
            )}
            <button type="button" onClick={onCopyValue} title={`Copiar ${label.toLowerCase()}`} disabled={!value}>
                <FaCopy />
            </button>
        </div>
    </div>
);

export const StepQuickAccess = ({
    projectId,
    subProjectId = null,
    scopeRecord = null,
    projectRecord = null,
    onScopePatched = null,
    variant = "main",
}) => {
    const isProjectScope = !subProjectId;
    const scopeId = isProjectScope ? projectId : subProjectId;
    const { useAccessPin } = useProjectAccess(projectId);

    const [useConfigOpen, setConfigOpen] = useState(false);
    const [useMode, setMode] = useState("LINK");
    const [useSourceKey, setSourceKey] = useState("");
    const [useSecretField, setSecretField] = useState(SECRET_TOKEN);
    const [useRevealed, setRevealed] = useState(null);
    const [useVaultPins, setVaultPins] = useState({});
    const [usePinModal, setPinModal] = useState(false);
    const [usePinValue, setPinValue] = useState("");
    const [usePinError, setPinError] = useState("");
    const [usePendingReveal, setPendingReveal] = useState(null);
    const [useLinks, setLinks] = useState(() => onReadLinks(scopeRecord));
    const [useActiveSlot, setActiveSlot] = useState(0);
    const [useEditingSlot, setEditingSlot] = useState(0);
    const [useMoreInfo, setMoreInfo] = useState(false);
    const [useVisibleSecrets, setVisibleSecrets] = useState({});
    const [useQuickCreate, setQuickCreate] = useState(() => onQuickDraft());

    const { apiGet: onGetProject, useApiData: PROJECT_DATA } = useApiQuery({ minTimeQuery: 120, enablePagination: false });
    const { apiGet: onGetCredentials, useApiData: CREDENTIALS } = useApiQuery({ minTimeQuery: 120, enablePagination: false });
    const { apiGet: onGetVault, useApiData: VAULT_GROUPS } = useApiQuery({ minTimeQuery: 120, enablePagination: false });
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({ minTimeMutate: 120, isToastError: true });

    useEffect(() => {
        const next = onReadLinks(scopeRecord);
        setLinks(next);
        setRevealed(null);
        setMoreInfo(false);
        setVisibleSecrets({});
        const populated = next.map((item, index) => item?.id ? index : -1).filter((index) => index >= 0);
        setActiveSlot((current) => populated.includes(current) ? current : (populated[0] ?? 0));
    }, [
        scopeRecord?.stepQuickCredentialSource,
        scopeRecord?.stepQuickCredentialId,
        scopeRecord?.stepQuickCredentialSecretField,
        scopeRecord?.stepQuickCredential2Source,
        scopeRecord?.stepQuickCredential2Id,
        scopeRecord?.stepQuickCredential2SecretField,
        scopeRecord?.stepQuickCredential3Source,
        scopeRecord?.stepQuickCredential3Id,
        scopeRecord?.stepQuickCredential3SecretField,
        scopeRecord?.stepQuickCredential4Source,
        scopeRecord?.stepQuickCredential4Id,
        scopeRecord?.stepQuickCredential4SecretField,
        scopeRecord?.stepQuickCredential5Source,
        scopeRecord?.stepQuickCredential5Id,
        scopeRecord?.stepQuickCredential5SecretField,
    ]);

    useEffect(() => {
        if (!projectId) return;
        if (!projectRecord?.companyId) {
            onGetProject({ endpoint: PROJECTS_CONSTANTS.DETAIL, isDetail: true, config: { pathParams: { id: projectId } } });
        }
        onGetCredentials({ endpoint: CREDENTIALS_CONSTANTS.BY_PROJECT, config: { pathParams: { projectId } } });
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const PROJECT = projectRecord?.companyId ? projectRecord : PROJECT_DATA;
    const companyId = PROJECT?.companyId || PROJECT?.company?.id || null;

    useEffect(() => {
        if (!companyId) return;
        onGetVault({ endpoint: VAULT_CONSTANTS.GROUP_BY_COMPANY, config: { pathParams: { companyId } } });
    }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const credentials = onAsArray(CREDENTIALS);
    const vaultEntries = useMemo(() => onFlattenVaultEntries(VAULT_GROUPS), [VAULT_GROUPS]);

    const allSources = useMemo(() => [
        ...credentials.map((item) => ({ ...item, __source: SOURCE_PROJECT })),
        ...vaultEntries,
    ], [credentials, vaultEntries]);

    const linkedSlotIndexes = useMemo(() => useLinks
        .map((item, index) => item?.source && item?.id ? index : -1)
        .filter((index) => index >= 0), [useLinks]);
    const linkedCount = linkedSlotIndexes.length;
    const activeOrdinal = Math.max(linkedSlotIndexes.indexOf(useActiveSlot), 0) + (linkedCount ? 1 : 0);
    const activeLink = useLinks[useActiveSlot] || EMPTY_LINK;

    const activeSource = allSources.find((item) => (
        item.__source === activeLink.source && String(item.id) === String(activeLink.id)
    )) || null;

    const selectedParsed = onParseSourceKey(useSourceKey);
    const selectedSource = allSources.find((item) => (
        item.__source === selectedParsed.source && String(item.id) === String(selectedParsed.id)
    )) || null;

    const sourceOptions = useMemo(() => {
        const projectOptions = credentials.map((item) => ({
            value: onSourceKey(SOURCE_PROJECT, item.id),
            label: item.title || item.username || "Credencial",
        }));
        const vaultOptions = vaultEntries.map((item) => ({
            value: onSourceKey(SOURCE_VAULT, item.id),
            label: `${item.title || item.username || "Acceso"}${item.__groupName ? ` · ${item.__groupName}` : ""}`,
        }));
        return [
            ...(projectOptions.length ? [{ label: "Credenciales del proyecto", options: projectOptions }] : []),
            ...(vaultOptions.length ? [{ label: "Bóveda de la compañía", options: vaultOptions }] : []),
        ];
    }, [credentials, vaultEntries]);

    useEffect(() => {
        if (!selectedSource) return;
        const available = onSecretOptions(selectedSource);
        if (!available.some((item) => item.value === useSecretField)) {
            setSecretField(available[0]?.value || SECRET_TOKEN);
        }
    }, [selectedSource]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setRevealed(null);
        setMoreInfo(false);
        setVisibleSecrets({});
    }, [useActiveSlot]);

    const primarySecretField = activeLink.secretField === SECRET_SECRET ? "secret" : "token";
    const maskedSecret = activeSource ? activeSource?.[primarySecretField] : "";
    const revealedSecret = onReadRevealedValue(useRevealed, primarySecretField);
    const secretValue = useVisibleSecrets[primarySecretField]
        ? (revealedSecret || "")
        : (maskedSecret || (activeSource ? "••••••••" : ""));
    const usernameValue = activeSource?.username || activeSource?.email || "";
    const hostValue = activeSource?.host || activeSource?.url || "";
    const isQuickCreated = activeSource?.__source === SOURCE_PROJECT
        && onPlainText(activeSource?.notes) === "Creada desde Acceso rápido de Pasos";
    const moreInfoRows = useMemo(() => onBuildMoreInfoRows(activeSource, useRevealed), [activeSource, useRevealed]);

    const editingLink = useLinks[useEditingSlot] || EMPTY_LINK;
    const editingSource = allSources.find((item) => (
        item.__source === editingLink.source && String(item.id) === String(editingLink.id)
    )) || null;
    const editingIsQuickCreated = editingSource?.__source === SOURCE_PROJECT
        && onPlainText(editingSource?.notes) === "Creada desde Acceso rápido de Pasos";

    const onMoveCredential = (delta) => {
        if (linkedCount < 2) return;
        const position = linkedSlotIndexes.indexOf(useActiveSlot);
        const current = position >= 0 ? position : 0;
        const next = (current + delta + linkedCount) % linkedCount;
        setActiveSlot(linkedSlotIndexes[next]);
    };

    const onOpenConfig = (slotIndex = useActiveSlot) => {
        const safeSlot = Math.min(Math.max(Number(slotIndex) || 0, 0), MAX_QUICK_LINKS - 1);
        const link = useLinks[safeSlot] || EMPTY_LINK;
        const source = allSources.find((item) => (
            item.__source === link.source && String(item.id) === String(link.id)
        )) || null;
        const isManualQuick = source?.__source === SOURCE_PROJECT
            && onPlainText(source?.notes) === "Creada desde Acceso r\u00e1pido de Pasos";

        setEditingSlot(safeSlot);
        setMode(isManualQuick ? "CREATE" : "LINK");
        setSourceKey(onSourceKey(link.source, link.id));
        setSecretField(link.secretField || SECRET_TOKEN);
        setQuickCreate(isManualQuick ? onQuickDraft(source) : onQuickDraft());
        setConfigOpen(true);
    };

    const onAddCredential = () => {
        if (linkedCount >= MAX_QUICK_LINKS) {
            REACT_HOT_TOAST_ERROR("Ya tenés las 5 credenciales rápidas permitidas");
            return;
        }
        const emptySlot = useLinks.findIndex((item) => !item?.id);
        onOpenConfig(emptySlot >= 0 ? emptySlot : linkedCount);
    };

    const onPatchLinks = async (links, nextActiveSlot = null) => {
        const normalized = onNormalizeLinks(links);
        const patch = onLinksPatch(normalized);
        const endpoint = isProjectScope ? PROJECTS_CONSTANTS.UPDATE : SUBPROJECTS_CONSTANTS.UPDATE;
        const response = await actionMutate({ method: "PATCH", endpoint, id: scopeId, data: patch });
        if (!response.success) return false;
        setLinks(normalized);
        setRevealed(null);
        setMoreInfo(false);
        setVisibleSecrets({});
        const populated = normalized.map((item, index) => item.id ? index : -1).filter((index) => index >= 0);
        const fallback = populated[0] ?? 0;
        const resolvedActive = nextActiveSlot !== null && populated.includes(nextActiveSlot) ? nextActiveSlot : fallback;
        setActiveSlot(resolvedActive);
        onScopePatched?.(patch);
        window.dispatchEvent(new Event("notebook:notifications-refresh"));
        return true;
    };

    const onSaveLink = async () => {
        const parsed = onParseSourceKey(useSourceKey);
        if (!parsed.source || !parsed.id) {
            REACT_HOT_TOAST_ERROR("Seleccioná una credencial o un acceso de la Bóveda");
            return;
        }
        const duplicate = useLinks.some((item, index) => index !== useEditingSlot
            && item?.source === parsed.source && String(item?.id) === String(parsed.id));
        if (duplicate) {
            REACT_HOT_TOAST_ERROR("Esa credencial ya está vinculada en Acceso rápido");
            return;
        }
        const draft = [...useLinks];
        draft[useEditingSlot] = { source: parsed.source, id: parsed.id, secretField: useSecretField };
        const compact = onNormalizeLinks(draft);
        const nextSlot = compact.findIndex((item) => item.source === parsed.source && String(item.id) === String(parsed.id));
        const ok = await onPatchLinks(compact, nextSlot);
        if (ok) {
            REACT_HOT_TOAST_SUCCESS("Acceso rápido vinculado");
            setConfigOpen(false);
        }
    };

    const onCreateQuickCredential = async () => {
        const title = useQuickCreate.title.trim();
        const token = useQuickCreate.token.trim();
        const isEditingQuick = !!editingIsQuickCreated && !!editingSource?.id;

        if (!title || (!isEditingQuick && !token)) {
            REACT_HOT_TOAST_ERROR(isEditingQuick
                ? "Indic\u00e1 un nombre para el acceso r\u00e1pido"
                : "Indic\u00e1 un nombre y el token / secreto");
            return;
        }

        const duplicateName = credentials.some((credential) => (
            String(credential?.id || "") !== String(editingSource?.id || "")
            && onCredentialNameKey(credential?.title) === onCredentialNameKey(title)
        ));
        if (duplicateName) {
            REACT_HOT_TOAST_ERROR(`Ya existe una credencial llamada "${title}" en este proyecto`);
            return;
        }

        const data = {
            title,
            type: "TOKEN",
            username: useQuickCreate.username.trim(),
            host: useQuickCreate.host.trim(),
            accessPin: useAccessPin || undefined,
            notes: "Creada desde Acceso r\u00e1pido de Pasos",
        };
        if (token) data.token = token;

        if (isEditingQuick) {
            const response = await actionMutate({
                method: "PATCH",
                endpoint: CREDENTIALS_CONSTANTS.UPDATE,
                id: editingSource.id,
                data,
            });
            if (!response.success) return;
            setRevealed(null);
            setVisibleSecrets({});
            setMoreInfo(false);
            setQuickCreate(onQuickDraft());
            setConfigOpen(false);
            await onGetCredentials({ endpoint: CREDENTIALS_CONSTANTS.BY_PROJECT, config: { pathParams: { projectId } } });
            REACT_HOT_TOAST_SUCCESS("Acceso r\u00e1pido actualizado");
            return;
        }

        const response = await actionMutate({
            method: "POST",
            endpoint: CREDENTIALS_CONSTANTS.CREATE,
            data: {
                projectId,
                ...data,
                token,
            },
        });
        if (!response.success || !response.data?.id) return;
        const created = response.data;
        const draft = [...useLinks];
        draft[useEditingSlot] = { source: SOURCE_PROJECT, id: created.id, secretField: SECRET_TOKEN };
        const compact = onNormalizeLinks(draft);
        const nextSlot = compact.findIndex((item) => item.source === SOURCE_PROJECT && String(item.id) === String(created.id));
        const ok = await onPatchLinks(compact, nextSlot);
        if (!ok) return;
        REACT_HOT_TOAST_SUCCESS("Credencial r\u00e1pida creada y vinculada");
        setQuickCreate(onQuickDraft());
        setConfigOpen(false);
        await onGetCredentials({ endpoint: CREDENTIALS_CONSTANTS.BY_PROJECT, config: { pathParams: { projectId } } });
    };

    const onRevealProjectCredential = async () => {
        const url = API_CORE.ENDPOINTS[CREDENTIALS_CONSTANTS.REVEAL]?.replace(":id", activeSource.id);
        if (!url) return null;
        const response = await API_CORE.SERVICES.POST_RESOURCE(url, { accessPin: useAccessPin || undefined });
        return response?.data ?? null;
    };

    const onRevealVaultEntry = async (pin = null) => {
        const url = API_CORE.ENDPOINTS[VAULT_CONSTANTS.ENTRY_REVEAL]?.replace(":id", activeSource.id);
        if (!url) return null;
        const response = await API_CORE.SERVICES.POST_RESOURCE(url, { accessPin: pin || undefined });
        return response?.data ?? null;
    };

    const onApplyRevealedAction = async (revealed, { field, forCopy = false } = {}) => {
        if (!revealed || !field) return;
        const value = onReadRevealedValue(revealed, field);
        if (forCopy) {
            await onCopy(value, field.startsWith("extra:") ? field.slice(6) : "Secreto");
            return;
        }
        setRevealed(revealed);
        setVisibleSecrets((current) => ({ ...current, [field]: true }));
    };

    const onDoReveal = async ({ field = primarySecretField, forCopy = false } = {}) => {
        if (!activeSource || !field) return;

        if (!forCopy && useVisibleSecrets[field]) {
            setVisibleSecrets((current) => ({ ...current, [field]: false }));
            return;
        }

        if (useRevealed && onReadRevealedValue(useRevealed, field)) {
            if (forCopy) await onCopy(onReadRevealedValue(useRevealed, field), field.startsWith("extra:") ? field.slice(6) : "Secreto");
            else setVisibleSecrets((current) => ({ ...current, [field]: true }));
            return;
        }

        try {
            if (activeSource.__source === SOURCE_PROJECT) {
                const revealed = await onRevealProjectCredential();
                await onApplyRevealedAction(revealed, { field, forCopy });
                return;
            }

            const cachedPin = useVaultPins[activeSource.__groupId] || null;
            if (activeSource.__groupHasPin && !cachedPin) {
                setPendingReveal({ field, forCopy });
                setPinValue("");
                setPinError("");
                setPinModal(true);
                return;
            }
            const revealed = await onRevealVaultEntry(cachedPin);
            await onApplyRevealedAction(revealed, { field, forCopy });
        } catch (error) {
            REACT_HOT_TOAST_ERROR(error?.response?.data?.message || "No se pudo revelar el acceso");
        }
    };

    const onVerifyVaultPin = async () => {
        if (!activeSource?.__groupId || !usePinValue.trim()) {
            setPinError("Escribí el PIN del grupo");
            return;
        }
        try {
            const verifyUrl = API_CORE.ENDPOINTS[VAULT_CONSTANTS.GROUP_VERIFY_PIN]?.replace(":id", activeSource.__groupId);
            const verify = await API_CORE.SERVICES.POST_RESOURCE(verifyUrl, { accessPin: usePinValue.trim() });
            if (!verify?.data?.isValid) {
                setPinError("El PIN no es correcto");
                return;
            }
            const pin = usePinValue.trim();
            setVaultPins((current) => ({ ...current, [activeSource.__groupId]: pin }));
            const revealed = await onRevealVaultEntry(pin);
            const pending = usePendingReveal || { field: primarySecretField, forCopy: false };
            setPinModal(false);
            setPinValue("");
            setPinError("");
            setPendingReveal(null);
            await onApplyRevealedAction(revealed, pending);
        } catch (error) {
            setPinError(error?.response?.data?.message || "No se pudo validar el PIN");
        }
    };

    const onOpenSource = (source) => {
        if (!source) return;
        const path = source.__source === SOURCE_VAULT
            ? (companyId && source.__groupId
                ? `/companies/${companyId}/vault/${source.__groupId}?focusEntry=${encodeURIComponent(source.id)}`
                : "")
            : `/projects/${projectId}/credentials?focusCredential=${encodeURIComponent(source.id)}`;
        if (!path) return;
        window.open(path, "_blank", "noopener,noreferrer");
    };

    const onArchiveQuickCredential = async (source = activeSource) => {
        const isManualQuick = source?.__source === SOURCE_PROJECT
            && onPlainText(source?.notes) === "Creada desde Acceso r\u00e1pido de Pasos";
        if (!source?.id || !isManualQuick) return;

        const isConfirmed = await confirm.danger(
            "Archivar acceso rápido",
            `Se archiva "${source.title || "este acceso"}" y queda disponible en Archivados para restaurarlo o eliminarlo definitivamente.`,
        );
        if (!isConfirmed) return;

        const response = await onApiAction({
            method: "PATCH",
            endpoint: CREDENTIALS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: source.id },
            data: { accessPin: useAccessPin || undefined },
        });
        if (!response.success) return;

        const sourceSlot = useLinks.findIndex((item) => (
            item?.source === SOURCE_PROJECT && String(item?.id) === String(source.id)
        ));
        const draft = [...useLinks];
        if (sourceSlot >= 0) draft[sourceSlot] = { ...EMPTY_LINK };
        const compact = onNormalizeLinks(draft);
        const nextIndex = compact.findIndex((item) => item?.id);
        const ok = await onPatchLinks(compact, nextIndex >= 0 ? nextIndex : 0);
        if (!ok) return;

        setConfigOpen(false);
        setQuickCreate(onQuickDraft());
        await onGetCredentials({ endpoint: CREDENTIALS_CONSTANTS.BY_PROJECT, config: { pathParams: { projectId } } });
        REACT_HOT_TOAST_SUCCESS("Acceso rápido archivado");
    };

    const onUnlinkEditingSlot = async () => {
        const draft = [...useLinks];
        draft[useEditingSlot] = { ...EMPTY_LINK };
        const compact = onNormalizeLinks(draft);
        const ok = await onPatchLinks(compact, Math.min(useEditingSlot, Math.max(linkedCount - 2, 0)));
        if (ok) {
            setSourceKey("");
            setConfigOpen(false);
            REACT_HOT_TOAST_SUCCESS("Credencial rápida desvinculada");
        }
    };

    return (
        <>
            <section className={`LStudio__app_steps_quick_access LStudio__app_steps_quick_access--${variant}`}>
                <div className="LStudio__app_steps_quick_access_head">
                    <div className="LStudio__app_steps_quick_access_identity">
                        <span className="LStudio__app_steps_quick_access_kicker"><FaKey /> Acceso rápido</span>
                        <strong>{activeSource?.title || (activeSource ? "Credencial vinculada" : "Sin credencial vinculada")}</strong>
                        {activeSource && (
                            <small>{isQuickCreated
                                ? "Acceso rápido manual"
                                : (activeSource.__source === SOURCE_VAULT ? `Bóveda · ${activeSource.__groupName || "Compañía"}` : "Credenciales del proyecto")}</small>
                        )}
                    </div>
                    <div className="LStudio__app_steps_quick_access_actions">
                        {linkedCount > 1 && (
                            <div className="LStudio__app_steps_quick_pager" aria-label="Cambiar credencial rápida">
                                <button type="button" onClick={() => onMoveCredential(-1)} title="Credencial anterior"><FaChevronLeft /></button>
                                <span>{activeOrdinal}/{linkedCount}</span>
                                <button type="button" onClick={() => onMoveCredential(1)} title="Credencial siguiente"><FaChevronRight /></button>
                            </div>
                        )}
                        {linkedCount < MAX_QUICK_LINKS && (
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPlus />} onClick={onAddCredential}>
                                Agregar
                            </AppButton>
                        )}
                        {isQuickCreated ? (
                            <>
                                <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEdit />} onClick={() => onOpenConfig(useActiveSlot)}>
                                    Editar
                                </AppButton>
                                <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />} onClick={() => onArchiveQuickCredential(activeSource)}>
                                    Archivar
                                </AppButton>
                            </>
                        ) : activeSource ? (
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaExternalLinkAlt />} onClick={() => onOpenSource(activeSource)}>
                                Gestionar
                            </AppButton>
                        ) : (
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaLink />} onClick={() => onOpenConfig(useActiveSlot)}>
                                Vincular
                            </AppButton>
                        )}
                    </div>
                </div>

                {activeSource ? (
                    <>
                        <div className="LStudio__app_steps_quick_fields">
                            <QuickValue icon={<FaUser />} label="Usuario" value={usernameValue}
                                onCopyValue={() => onCopy(usernameValue, "Usuario")} />
                            <QuickValue icon={<FaKey />} label={activeLink.secretField === SECRET_SECRET ? "Contraseña / secreto" : "Token"}
                                value={secretValue} isSecret isRevealed={!!useVisibleSecrets[primarySecretField]}
                                onReveal={() => onDoReveal({ field: primarySecretField, forCopy: false })}
                                onCopyValue={() => onDoReveal({ field: primarySecretField, forCopy: true })} />
                            <QuickValue icon={<FaServer />} label="Host / URL" value={hostValue}
                                onCopyValue={() => onCopy(hostValue, "Host / URL")} />
                        </div>

                        {!isQuickCreated && (
                            <div className="LStudio__app_steps_quick_morebar">
                                <button type="button" onClick={() => setMoreInfo((current) => !current)}>
                                    <FaInfoCircle /> Más información
                                    {useMoreInfo ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                            </div>
                        )}

                        {!isQuickCreated && useMoreInfo && (
                            <div className="LStudio__app_steps_quick_more">
                                {moreInfoRows.length > 0 ? moreInfoRows.map((row) => {
                                    const isVisible = !row.isSecret || !!useVisibleSecrets[row.field];
                                    const displayValue = row.isSecret && !isVisible ? "••••••••" : row.value;
                                    return (
                                        <div key={`${row.field}-${row.label}`} className="LStudio__app_steps_quick_more_row" data-secret={row.isSecret}>
                                            <span>{row.label}</span>
                                            <strong title={isVisible ? row.value : undefined}>{displayValue}</strong>
                                            <div className="LStudio__app_steps_quick_more_actions">
                                                {row.isSecret && (
                                                    <button type="button" onClick={() => onDoReveal({ field: row.field, forCopy: false })}
                                                        title={isVisible ? `Ocultar ${row.label.toLowerCase()}` : `Revelar ${row.label.toLowerCase()}`}>
                                                        {isVisible ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                )}
                                                <button type="button"
                                                    onClick={() => row.isSecret
                                                        ? onDoReveal({ field: row.field, forCopy: true })
                                                        : onCopy(row.value, row.label)}
                                                    title={`Copiar ${row.label.toLowerCase()}`}>
                                                    <FaCopy />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <span className="LStudio__app_steps_quick_more_empty">Esta credencial no tiene otros campos cargados.</span>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <button type="button" className="LStudio__app_steps_quick_empty" onClick={onAddCredential}>
                        <FaPlus /> Vinculá hasta 5 credenciales del proyecto o accesos de la Bóveda para tener usuario, token y host a mano durante el deploy.
                    </button>
                )}
            </section>

            <AppModal
                isOpen={useConfigOpen}
                onClose={() => setConfigOpen(false)}
                size="lg"
                title={`Acceso rápido de Pasos · credencial ${Math.min(useEditingSlot + 1, MAX_QUICK_LINKS)} de ${MAX_QUICK_LINKS}`}
                description={editingIsQuickCreated
                    ? "Este acceso fue creado directamente desde Pasos. Pod\u00e9s editarlo ac\u00e1 o reemplazarlo por una credencial existente."
                    : "Vincul\u00e1, reemplaz\u00e1 o administr\u00e1 una credencial. Los datos originales siguen viviendo en Credenciales o B\u00f3veda; Pasos s\u00f3lo conserva el v\u00ednculo."}
                footer={(
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => setConfigOpen(false)}>Cancelar</AppButton>
                        {useMode === "LINK" ? (
                            <AppButton onClick={onSaveLink} isLoading={useActionApiIsLoading}>{editingSource ? "Actualizar vínculo" : "Guardar vínculo"}</AppButton>
                        ) : (
                            <AppButton onClick={onCreateQuickCredential} isLoading={useActionApiIsLoading}>
                                {editingIsQuickCreated ? "Guardar cambios" : "Crear y vincular"}
                            </AppButton>
                        )}
                    </>
                )}
            >
                {editingSource && (
                    <div className="LStudio__app_steps_quick_manage_context">
                        <div>
                            <span>Vínculo actual</span>
                            <strong>{editingSource.title || editingSource.username || "Credencial"}</strong>
                            <small>{editingIsQuickCreated
                                ? "Acceso rápido manual"
                                : (editingSource.__source === SOURCE_VAULT ? `Bóveda · ${editingSource.__groupName || "Compañía"}` : "Credenciales del proyecto")}</small>
                        </div>
                        {!editingIsQuickCreated && (
                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaExternalLinkAlt />} onClick={() => onOpenSource(editingSource)}>
                                {editingSource.__source === SOURCE_VAULT ? "Abrir en B\u00f3veda" : "Abrir en Credenciales"}
                            </AppButton>
                        )}
                    </div>
                )}
                <div className="LStudio__app_steps_quick_mode">
                    {editingIsQuickCreated ? (
                        <>
                            <button type="button" data-active={useMode === "CREATE"}
                                onClick={() => { setMode("CREATE"); setQuickCreate(onQuickDraft(editingSource)); }}>Editar acceso rápido</button>
                            <button type="button" data-active={useMode === "LINK"}
                                onClick={() => { setMode("LINK"); setSourceKey(""); }}>Reemplazar por existente</button>
                        </>
                    ) : (
                        <>
                            <button type="button" data-active={useMode === "LINK"} onClick={() => setMode("LINK")}>Usar existente</button>
                            <button type="button" data-active={useMode === "CREATE"}
                                onClick={() => { setMode("CREATE"); setQuickCreate(onQuickDraft()); }}>Crear rápida</button>
                        </>
                    )}
                </div>
                {editingSource && useMode === "LINK" && (
                    <p className="LStudio__app_steps_quick_manage_hint">
                        {editingIsQuickCreated
                            ? "Eleg\u00ed otra credencial si quer\u00e9s reemplazar este acceso r\u00e1pido manual."
                            : "Cambi\u00e1 el v\u00ednculo desde ac\u00e1. Para modificar usuario, contrase\u00f1a, token u otros datos de la credencial original, abrila en su m\u00f3dulo."}
                    </p>
                )}
                {editingIsQuickCreated && useMode === "CREATE" && (
                    <p className="LStudio__app_steps_quick_manage_hint">
                        Editá este acceso directamente desde Pasos. Si dejás el token vacío, se conserva el secreto actual.
                    </p>
                )}

                {useMode === "LINK" ? (
                    <FormGrid columns={2}>
                        <FieldSelect
                            label="Origen"
                            value={useSourceKey}
                            onChange={(event) => setSourceKey(event.target.value)}
                            options={sourceOptions}
                            placeholder="Elegí Credenciales o Bóveda..."
                            isSearchable
                            span={2}
                        />
                        <FieldSelect
                            label="Dato secreto a mostrar"
                            value={useSecretField}
                            onChange={(event) => setSecretField(event.target.value)}
                            options={onSecretOptions(selectedSource)}
                            placeholder={selectedSource ? "Elegí token o secreto" : "Primero elegí el origen"}
                            isDisabled={!selectedSource}
                            span={2}
                        />
                    </FormGrid>
                ) : (
                    <FormGrid columns={2}>
                        <FieldInput label="Nombre" value={useQuickCreate.title}
                            onChange={(event) => setQuickCreate((current) => ({ ...current, title: event.target.value }))} span={2} />
                        <FieldInput label="Usuario" value={useQuickCreate.username}
                            onChange={(event) => setQuickCreate((current) => ({ ...current, username: event.target.value }))} />
                        <FieldInput label="Host / URL" value={useQuickCreate.host}
                            onChange={(event) => setQuickCreate((current) => ({ ...current, host: event.target.value }))} />
                        <FieldInput label="Token / secreto" type="password" value={useQuickCreate.token}
                            placeholder={editingIsQuickCreated ? "Dejar vac\u00edo conserva el token / secreto actual" : "Token / secreto"}
                            onChange={(event) => setQuickCreate((current) => ({ ...current, token: event.target.value }))} span={2} />
                    </FormGrid>
                )}

                {useLinks[useEditingSlot]?.id && (
                    <div className="LStudio__app_steps_quick_unlink">
                        <AppButton size="sm" variant="ghost" tone="neutral" onClick={onUnlinkEditingSlot}>Desvincular esta credencial</AppButton>
                        {editingIsQuickCreated && (
                            <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />}
                                onClick={() => onArchiveQuickCredential(editingSource)}>
                                Archivar acceso rápido
                            </AppButton>
                        )}
                    </div>
                )}
            </AppModal>

            <AppModal
                isOpen={usePinModal}
                onClose={() => { setPinModal(false); setPinError(""); setPendingReveal(null); }}
                size="sm"
                title="PIN de la Bóveda"
                description={`El grupo ${activeSource?.__groupName || "seleccionado"} está protegido.`}
                footer={(
                    <>
                        <AppButton variant="ghost" tone="neutral" onClick={() => setPinModal(false)}>Cancelar</AppButton>
                        <AppButton onClick={onVerifyVaultPin}>Continuar</AppButton>
                    </>
                )}
            >
                <FieldInput label="PIN" type="password" value={usePinValue}
                    onChange={(event) => { setPinValue(event.target.value); setPinError(""); }} error={usePinError} />
            </AppModal>
        </>
    );
};
